import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission-service/permission-service';
import { PERMISSIONS } from '../../../core/constants/permissions.constants';

/**
 * Factory function to create a permission guard for a specific permission
 * @param permissionName - The permission key from PERMISSIONS constant
 * @returns CanActivateFn guard function
 */
export function permissionGuard(permissionName: keyof typeof PERMISSIONS): CanActivateFn {
  return async () => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    const hasPermission = await permissionService.getPermissionByName(PERMISSIONS[permissionName]);

    if (hasPermission) {
      return true;
    } else {
      router.navigate(['/unauthorized']);
      return false;
    }
  };
}

// Pre-defined guards for commonly used permissions
export const bulkEditGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const hasPermission = await permissionService.getPermissionByName(PERMISSIONS.BULK_EDIT);

  if (hasPermission) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};

export const skillTreeGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const hasPermission = await permissionService.getPermissionByName(PERMISSIONS.SKILL_TREE_CREATE_READ);

  if (hasPermission) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};

export const createContentGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const hasPermission = await permissionService.getPermissionByName(PERMISSIONS.CREATE_CONTENT);

  if (hasPermission) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};

export const editContentGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const canEditOwnContent = await permissionService.getPermissionByName(PERMISSIONS.EDIT_OWN_CONTENT);
  const canEditAnyContent = await permissionService.getPermissionByName(PERMISSIONS.EDIT_ANY_CONTENT);

  if (canEditOwnContent || canEditAnyContent) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};

export const createProjectGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const hasPermission = await permissionService.getPermissionByName(PERMISSIONS.CREATE_NEW_PROJECT);

  if (hasPermission) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};

export const editProjectGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const editProjectPermission = await permissionService.getPermissionByName(PERMISSIONS.EDIT_PROJECT_CONFIGURATION);
  const assignProjectRolesPermission= await permissionService.getPermissionByName(PERMISSIONS.ASSIGN_PROJECT_ROLES);

  if (editProjectPermission || assignProjectRolesPermission) {
    return true;
  } else {
    router.navigate(['/unauthorized']);
    return false;
  }
};
